import React from 'react';
import MenuItem from '@mui/material/MenuItem';
import Icon from '@mui/material/Icon';
import Divider from '@mui/material/Divider';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import { useTheme as useMuiTheme } from '@mui/material/styles';
import MDSelect from 'components/MDSelect';

import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDInput from 'components/MDInput';
import MDButton from 'components/MDButton';

function emptyPlanDraft() {
  return { name: '', priceTry: '', classCapacity: 1, durationMonths: 1, items: [] };
}

function numOr0(x) {
  if (x === '' || x == null) return 0; // ✅ backend priceTry null istemiyor -> 0
  const n = Number(x);
  return Number.isFinite(n) ? n : 0;
}
function numOr1(x) {
  if (x === '' || x == null) return 1;
  const n = Number(x);
  return Number.isFinite(n) ? n : 1;
}
function intOrDefault(x, def = 0) {
  if (x === '' || x == null) return def;
  const n = Number(x);
  return Number.isFinite(n) ? Math.trunc(n) : def;
}

function toPayload(draft, findActName) {
  return {
    name: String(draft.name || '').trim(),
    priceTry: intOrDefault(draft.priceTry, 0),
    classCapacity: Math.max(1, intOrDefault(draft.classCapacity, 1)),
    durationMonths: Math.max(1, intOrDefault(draft.durationMonths, 1)), // ✅ EKLENDİ

    items: Array.isArray(draft.items)
      ? draft.items
          .filter((it) => it.activityId != null && String(it.activityId) !== '')
          .map((it) => {
            const activityName = String(findActName?.(it.activityId) || '').trim();

            return {
              // backend artık activityId istemiyorsa kaldırabilirsin,
              // ama FE tarafında name’i bulmak için yine id gerekiyor.
              // activityId: Number(it.activityId),

              activityName, // ✅ ZORUNLU
              sessions: Math.max(1, intOrDefault(it.sessions, 1)),
              makeUp: Math.max(0, intOrDefault(it.makeUp, 0)),
              totalGift: it.gift ? 1 : 0, // ✅ backend giftBool/giftCount ile uyum
            };
          })
          .filter((x) => x.activityName.length > 0) // ✅ boş adları at
      : [],
  };
}

const CREATE_ACTIVITY_VALUE = '__CREATE_ACTIVITY__';

export function PlanBuilder({
  mode, // CREATE | CUSTOM | EDIT
  initialPlan,
  editingPlanId,

  activities, // [{id,name,active}]
  onCreateActivity, // async(name)
  onUpdateActivity, // async(id,{name})
  onDeleteActivity, // async(id)

  onSave, // async(payload) => createdPlan
  onUpdate, // async(planId,payload)

  onDone, // ✅ NEW: (result) => void
  onCloseBuilder,
}) {
  const theme = useMuiTheme();

  const [draft, setDraft] = React.useState(emptyPlanDraft());

  // create activity modal
  const [createActOpen, setCreateActOpen] = React.useState(false);
  const [newActivityName, setNewActivityName] = React.useState('');

  // edit activity modal
  const [editActOpen, setEditActOpen] = React.useState(false);
  const [editActId, setEditActId] = React.useState(null);
  const [editActName, setEditActName] = React.useState('');

  // confirm delete modal
  const [confirmDelOpen, setConfirmDelOpen] = React.useState(false);
  const [delActId, setDelActId] = React.useState(null);

  React.useEffect(() => {
    if (mode === 'EDIT' && initialPlan) {
      setDraft({
        name: initialPlan.name ?? '',
        priceTry: initialPlan.priceTry ?? 0,
        classCapacity: initialPlan.classCapacity ?? 1,
        durationMonths: initialPlan.durationMonths ?? 1,
        items: Array.isArray(initialPlan.items)
          ? initialPlan.items.map((it) => ({
              activityId: it.activityId,
              sessions: it.sessions ?? 1,
              makeUp: it.makeUp ?? 0,
              gift: Boolean(it.gift),
            }))
          : [],
      });
    } else {
      setDraft(emptyPlanDraft());
    }
  }, [mode, initialPlan]);

  const items = React.useMemo(() => (Array.isArray(draft.items) ? draft.items : []), [draft.items]);

  const activeActivities = React.useMemo(
    () => (activities || []).filter((a) => a?.active !== false),
    [activities]
  );

  const findActName = React.useCallback(
    (id) => {
      // ✅ activityName snapshot varsa onu zaten plan item’da da tutuyorsun,
      // ama burada select label için listeden buluyoruz.
      const a = (activities || []).find((x) => String(x?.id) === String(id));
      return a?.name || '';
    },
    [activities]
  );

  const validItemCount = items.filter(
    (x) => x.activityId != null && String(x.activityId) !== ''
  ).length;
  const canSave = String(draft.name || '').trim().length >= 3 && validItemCount > 0;

  const addRow = () => {
    setDraft((s) => ({
      ...s,
      items: [
        ...(Array.isArray(s.items) ? s.items : []),
        { activityId: '', sessions: 1, makeUp: 0, gift: false },
      ],
    }));
  };

  const setRow = (idx, patch) => {
    setDraft((s) => {
      const next = [...(Array.isArray(s.items) ? s.items : [])];
      next[idx] = { ...next[idx], ...patch };
      return { ...s, items: next };
    });
  };

  const removeRow = (idx) => {
    setDraft((s) => {
      const next = [...(Array.isArray(s.items) ? s.items : [])];
      next.splice(idx, 1);
      return { ...s, items: next };
    });
  };

  const panelStyle = {
    borderRadius: '18px',
    border: `1px solid ${theme.palette.divider}`,
    background:
      theme.palette.mode === 'dark'
        ? 'linear-gradient(180deg, rgba(255,255,255,.035), rgba(255,255,255,.02))'
        : 'linear-gradient(180deg, rgba(0,0,0,.02), rgba(0,0,0,.01))',
    p: 1.5,
  };

  const rowStyle = {
    borderRadius: '16px',
    border: `1px solid ${theme.palette.divider}`,
    background: theme.palette.background.paper,
    p: 1,
  };

  // ✅ Select içindeki ikonlar %100 çalışsın (MenuItem seçimini tetiklemesin)
  const stop = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const createCanSave = String(newActivityName || '').trim().length >= 2;
  const editCanSave = String(editActName || '').trim().length >= 2;

  const handleSavePlan = async () => {
    const payload = toPayload(draft, findActName);
    console.log('🧾 PLAN SAVE PAYLOAD', payload, findActName);

    // EDIT -> update
    if (mode === 'EDIT') {
      if (!editingPlanId) return;
      const updated = await onUpdate?.(editingPlanId, payload);

      onDone?.({
        type: 'LIBRARY_EDITED',
        planId: editingPlanId,
        payload,
        updated,
      });
      return;
    }

    // ✅ CUSTOM -> DB yok, sadece üyeye uygulanacak
    if (mode === 'CUSTOM') {
      onDone?.({
        type: 'CUSTOM',
        payload,
      });
      return;
    }

    // CREATE -> DB’ye kaydet
    const created = await onSave?.(payload);

    onDone?.({
      type: 'LIBRARY',
      planId: created?.id,
      payload,
      created,
    });
  };

  const openEditActivity = (a) => {
    setEditActId(a?.id ?? null);
    setEditActName(a?.name ?? '');
    setEditActOpen(true);
  };

  const askDeleteActivity = (a) => {
    setDelActId(a?.id ?? null);
    setConfirmDelOpen(true);
  };

  const doDeleteActivity = async () => {
    const id = delActId;
    setConfirmDelOpen(false);
    setDelActId(null);
    if (!id) return;

    // seçili satırlarda bu activity varsa temizle
    setDraft((s) => ({
      ...s,
      items: (Array.isArray(s.items) ? s.items : []).map((it) =>
        String(it.activityId) === String(id) ? { ...it, activityId: '' } : it
      ),
    }));

    await onDeleteActivity?.(id);
  };

  return (
    <MDBox
      sx={{
        borderRadius: '20px',
        border: `1px solid ${theme.palette.divider}`,
        background: theme.palette.background.paper,
        boxShadow: theme.shadows[2],
        overflow: 'hidden',
      }}
    >
      {/* Üst bilgi bandı */}
      <MDBox
        sx={{
          px: 2,
          py: 1.25,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          background: theme.palette.action.hover,
          borderBottom: `1px solid ${theme.palette.divider}`,
          flexWrap: 'wrap',
        }}
      >
        <MDBox display="flex" alignItems="center" gap={1}>
          <Icon sx={{ fontSize: 18, color: theme.palette.info.main }}>info</Icon>
          <MDTypography variant="button" sx={{ color: 'text.secondary' }}>
            Plan adı + en az 1 faaliyet kalemi seçilince kaydedebilirsin.
          </MDTypography>
        </MDBox>

        <MDButton
          variant="text"
          color="dark"
          onClick={onCloseBuilder}
          sx={{ borderRadius: '12px' }}
        >
          Kapat
        </MDButton>
      </MDBox>

      <MDBox sx={{ p: 2, display: 'grid', gap: 1.25 }}>
        {/* Plan Bilgileri */}
        <MDBox sx={panelStyle}>
          <MDBox display="flex" alignItems="center" gap={1} sx={{ mb: 1 }}>
            <Icon sx={{ fontSize: 18, color: theme.palette.info.main }}>tune</Icon>
            <MDTypography fontWeight="bold">Plan Bilgileri</MDTypography>
          </MDBox>

          <MDBox
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1.4fr .8fr .8fr .8fr' },
              gap: 1.1,
            }}
          >
            <MDInput
              label="Plan adı"
              value={draft.name}
              onChange={(e) => setDraft((s) => ({ ...s, name: e.target.value }))}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '14px' } }}
            />
            <MDInput
              label="Fiyat (₺)"
              type="number"
              value={draft.priceTry}
              onChange={(e) => setDraft((s) => ({ ...s, priceTry: e.target.value }))}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '14px' } }}
            />
            <MDInput
              label="Kapasite"
              type="number"
              value={draft.classCapacity}
              onChange={(e) => setDraft((s) => ({ ...s, classCapacity: e.target.value }))}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '14px' } }}
            />
            <MDInput
              label="Geçerlilik (Ay)"
              type="number"
              value={draft.durationMonths}
              onChange={(e) => setDraft((s) => ({ ...s, durationMonths: e.target.value }))}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '14px' } }}
              inputProps={{ min: 1 }}
            />
          </MDBox>
        </MDBox>

        {/* Faaliyet Kalemleri */}
        <MDBox sx={panelStyle}>
          <MDBox
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            sx={{ mb: 1, gap: 1, flexWrap: 'wrap' }}
          >
            <MDBox display="flex" alignItems="center" gap={1}>
              <Icon sx={{ fontSize: 18, color: theme.palette.warning.main }}>
                format_list_bulleted
              </Icon>
              <MDTypography fontWeight="bold">Faaliyet Kalemleri</MDTypography>
            </MDBox>

            <MDButton
              variant="outlined"
              color="warning"
              sx={{ borderRadius: '12px' }}
              onClick={addRow}
            >
              <Icon sx={{ mr: 0.5, fontSize: 18 }}>add</Icon>
              Faaliyet Ekle
            </MDButton>
          </MDBox>

          {items.length === 0 ? (
            <MDTypography variant="caption" sx={{ color: 'text.secondary' }}>
              Henüz faaliyet eklenmedi. “Faaliyet Ekle” ile başlayabilirsin.
            </MDTypography>
          ) : null}

          <MDBox sx={{ display: 'grid', gap: 0.9 }}>
            {items.map((row, idx) => (
              <MDBox key={idx} sx={rowStyle}>
                <MDBox
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: '1.55fr .55fr .55fr .7fr auto' },
                    gap: 1,
                    alignItems: 'center',
                  }}
                >
                  <MDSelect
                    value={row.activityId ?? ''}
                    renderValue={(v) => {
                      if (!v) return 'Faaliyet seç';
                      return findActName(v) || 'Faaliyet seç';
                    }}
                    onChange={(e) => {
                      const v = e.target.value;
                      if (v === CREATE_ACTIVITY_VALUE) {
                        setCreateActOpen(true);
                        return;
                      }
                      setRow(idx, { activityId: v });
                    }}
                    sx={{ minWidth: { xs: '100%', md: 360 }, borderRadius: '14px' }}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          borderRadius: '16px',
                          mt: 1,
                          border: `1px solid ${theme.palette.divider}`,
                          boxShadow: theme.shadows[8],
                          minWidth: { xs: '92vw', sm: 520, md: 640 },
                          maxHeight: 360,
                          overflow: 'auto',
                          p: 0.5,
                        },
                      },
                      MenuListProps: { sx: { p: 0.5 } },
                    }}
                  >
                    <MenuItem value="">
                      <MDTypography variant="button" sx={{ color: 'text.secondary' }}>
                        Faaliyet seç
                      </MDTypography>
                    </MenuItem>

                    {activeActivities.map((a) => (
                      <MenuItem key={a.id} value={a.id} sx={{ py: 1.05 }}>
                        <MDBox
                          sx={{
                            width: '100%',
                            display: 'grid',
                            gridTemplateColumns: '1fr auto',
                            gap: 1,
                            alignItems: 'center',
                          }}
                        >
                          <MDTypography
                            sx={{
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            }}
                          >
                            {a.name}
                          </MDTypography>

                          {/* ✅ DÜZENLE / SİL: IconButton seviyesinde stop */}
                          <MDBox sx={{ display: 'flex', gap: 0.25 }}>
                            <Tooltip title="Düzenle">
                              <IconButton
                                size="small"
                                onMouseDown={stop}
                                onClick={(e) => {
                                  stop(e);
                                  openEditActivity(a);
                                }}
                              >
                                <Icon sx={{ fontSize: 18, color: theme.palette.info.main }}>
                                  edit
                                </Icon>
                              </IconButton>
                            </Tooltip>

                            <Tooltip title="Sil">
                              <IconButton
                                size="small"
                                onMouseDown={stop}
                                onClick={(e) => {
                                  stop(e);
                                  askDeleteActivity(a);
                                }}
                              >
                                <Icon sx={{ fontSize: 18, color: theme.palette.error.main }}>
                                  delete
                                </Icon>
                              </IconButton>
                            </Tooltip>
                          </MDBox>
                        </MDBox>
                      </MenuItem>
                    ))}

                    <Divider sx={{ my: 0.75 }} />

                    <MenuItem value={CREATE_ACTIVITY_VALUE} sx={{ py: 1.05 }}>
                      <MDBox display="flex" alignItems="center" gap={1}>
                        <Icon sx={{ fontSize: 20, color: theme.palette.info.main }}>
                          add_circle
                        </Icon>
                        <MDTypography fontWeight="bold" sx={{ color: theme.palette.info.main }}>
                          Yeni faaliyet oluştur
                        </MDTypography>
                      </MDBox>
                    </MenuItem>
                  </MDSelect>

                  <MDInput
                    label="Seans"
                    type="number"
                    value={row.sessions}
                    onChange={(e) => setRow(idx, { sessions: e.target.value })}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '14px' } }}
                  />

                  <MDInput
                    label="Telafi"
                    type="number"
                    value={row.makeUp}
                    onChange={(e) => setRow(idx, { makeUp: e.target.value })}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '14px' } }}
                  />

                  <FormControlLabel
                    control={
                      <Switch
                        checked={Boolean(row.gift)}
                        onChange={(e) => setRow(idx, { gift: e.target.checked })}
                        color="success"
                      />
                    }
                    label="Hediye"
                  />

                  <Tooltip title="Satırı Sil">
                    <IconButton onClick={() => removeRow(idx)}>
                      <Icon sx={{ color: theme.palette.error.main }}>delete</Icon>
                    </IconButton>
                  </Tooltip>
                </MDBox>
              </MDBox>
            ))}
          </MDBox>
        </MDBox>

        {/* sadece Kaydet */}
        <MDBox sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <MDButton
            variant="gradient"
            color="success"
            sx={{ borderRadius: '14px' }}
            disabled={!canSave}
            onClick={handleSavePlan}
          >
            <Icon sx={{ mr: 0.5, fontSize: 18 }}>save</Icon>
            Kaydet
          </MDButton>
        </MDBox>

        <MDTypography variant="caption" sx={{ color: 'text.secondary' }}>
          * Plan adı + en az 1 faaliyet kalemi seçilmelidir.
        </MDTypography>
      </MDBox>

      {/* Faaliyet Oluştur */}
      <Dialog
        open={createActOpen}
        onClose={() => setCreateActOpen(false)}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          sx: {
            borderRadius: '18px',
            border: `1px solid ${theme.palette.divider}`,
            overflow: 'hidden',
          },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <MDBox display="flex" alignItems="center" justifyContent="space-between">
            <MDBox display="flex" alignItems="center" gap={1}>
              <Icon sx={{ fontSize: 18, color: theme.palette.info.main }}>add_circle</Icon>
              <MDTypography fontWeight="bold">Faaliyet Oluştur</MDTypography>
            </MDBox>
            <IconButton onClick={() => setCreateActOpen(false)}>
              <Icon sx={{ color: theme.palette.text.secondary }}>close</Icon>
            </IconButton>
          </MDBox>
        </DialogTitle>

        <DialogContent sx={{ pt: 0, pb: 2 }}>
          <MDBox sx={{ display: 'grid', gap: 1.25 }}>
            <MDInput
              label="Faaliyet adı"
              placeholder="Pilates, Yoga, Running..."
              value={newActivityName}
              onChange={(e) => setNewActivityName(e.target.value)}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '14px' } }}
            />

            <MDBox display="flex" justifyContent="flex-end" gap={1}>
              <MDButton
                variant="outlined"
                color="dark"
                sx={{ borderRadius: '12px' }}
                onClick={() => setCreateActOpen(false)}
              >
                Vazgeç
              </MDButton>

              <MDButton
                variant="gradient"
                color="info"
                sx={{ borderRadius: '12px' }}
                disabled={!createCanSave}
                onClick={async () => {
                  const name = String(newActivityName || '').trim();
                  if (name.length < 2) return;
                  await onCreateActivity?.(name);
                  setNewActivityName('');
                  setCreateActOpen(false);
                }}
              >
                <Icon sx={{ mr: 0.5, fontSize: 18 }}>save</Icon>
                Kaydet
              </MDButton>
            </MDBox>
          </MDBox>
        </DialogContent>
      </Dialog>

      {/* Faaliyet Düzenle */}
      <Dialog
        open={editActOpen}
        onClose={() => setEditActOpen(false)}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          sx: {
            borderRadius: '18px',
            border: `1px solid ${theme.palette.divider}`,
            overflow: 'hidden',
          },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <MDBox display="flex" alignItems="center" justifyContent="space-between">
            <MDBox display="flex" alignItems="center" gap={1}>
              <Icon sx={{ fontSize: 18, color: theme.palette.info.main }}>edit</Icon>
              <MDTypography fontWeight="bold">Faaliyet Düzenle</MDTypography>
            </MDBox>
            <IconButton onClick={() => setEditActOpen(false)}>
              <Icon sx={{ color: theme.palette.text.secondary }}>close</Icon>
            </IconButton>
          </MDBox>
        </DialogTitle>

        <DialogContent sx={{ pt: 0, pb: 2 }}>
          <MDBox sx={{ display: 'grid', gap: 1.25 }}>
            <MDInput
              label="Faaliyet adı"
              value={editActName}
              onChange={(e) => setEditActName(e.target.value)}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '14px' } }}
            />

            <MDBox display="flex" justifyContent="flex-end" gap={1}>
              <MDButton
                variant="outlined"
                color="dark"
                sx={{ borderRadius: '12px' }}
                onClick={() => setEditActOpen(false)}
              >
                Vazgeç
              </MDButton>

              <MDButton
                variant="gradient"
                color="info"
                sx={{ borderRadius: '12px' }}
                disabled={!editCanSave || !editActId}
                onClick={async () => {
                  const name = String(editActName || '').trim();
                  if (name.length < 2 || !editActId) return;
                  await onUpdateActivity?.(editActId, { name });
                  setEditActOpen(false);
                }}
              >
                <Icon sx={{ mr: 0.5, fontSize: 18 }}>save</Icon>
                Kaydet
              </MDButton>
            </MDBox>
          </MDBox>
        </DialogContent>
      </Dialog>

      {/* Sil confirm */}
      <Dialog
        open={confirmDelOpen}
        onClose={() => setConfirmDelOpen(false)}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          sx: {
            borderRadius: '18px',
            border: `1px solid ${theme.palette.divider}`,
            overflow: 'hidden',
          },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <MDBox display="flex" alignItems="center" gap={1}>
            <Icon sx={{ fontSize: 18, color: theme.palette.error.main }}>warning</Icon>
            <MDTypography fontWeight="bold">Emin misin?</MDTypography>
          </MDBox>
        </DialogTitle>

        <DialogContent sx={{ pt: 0, pb: 2 }}>
          <MDTypography variant="button" sx={{ color: 'text.secondary' }}>
            Bu faaliyeti silmek istediğine emin misin? Bu faaliyet seçili satırlarda temizlenecek.
          </MDTypography>

          <MDBox display="flex" justifyContent="flex-end" gap={1} sx={{ mt: 2 }}>
            <MDButton
              variant="outlined"
              color="dark"
              sx={{ borderRadius: '12px' }}
              onClick={() => setConfirmDelOpen(false)}
            >
              Vazgeç
            </MDButton>

            <MDButton
              variant="gradient"
              color="error"
              sx={{ borderRadius: '12px' }}
              onClick={doDeleteActivity}
            >
              <Icon sx={{ mr: 0.5, fontSize: 18 }}>delete</Icon>
              Sil
            </MDButton>
          </MDBox>
        </DialogContent>
      </Dialog>
    </MDBox>
  );
}
